import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useCampaigns, markSessionRefunded } from '../lib/api';
import { Session } from '../lib/types';

interface MarkRefundedModalProps {
    isOpen: boolean;
    onClose: () => void;
    sessionIds: string[];
    sessions?: Session[]; // Optional: for displaying session details
    onSuccess: () => void;
}

export const MarkRefundedModal: React.FC<MarkRefundedModalProps> = ({
    isOpen,
    onClose,
    sessionIds,
    sessions = [],
    onSuccess
}) => {
    const { campaigns, isLoading: campaignsLoading } = useCampaigns();
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
    const [reference, setReference] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successCount, setSuccessCount] = useState<number>(0);
    const [failedCount, setFailedCount] = useState<number>(0);
    const [errors, setErrors] = useState<string[]>([]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedCampaignId('');
            setReference('');
            setError(null);
            setSuccessCount(0);
            setFailedCount(0);
            setErrors([]);
        }
    }, [isOpen]);

    const handleClose = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    const handleSubmit = async () => {
        if (!selectedCampaignId) {
            setError('Please select a campaign');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setSuccessCount(0);
        setFailedCount(0);
        setErrors([]);

        let success = 0;
        let failed = 0;
        const errorMessages: string[] = [];

        // Process refunds sequentially for better error handling
        for (const sessionId of sessionIds) {
            try {
                await markSessionRefunded(sessionId, selectedCampaignId, reference || undefined);
                success++;
            } catch (err: any) {
                failed++;
                const errorMsg = err.message || 'Failed to mark session as refunded';
                errorMessages.push(`Session ${sessionId.substring(0, 8)}...: ${errorMsg}`);
            }
        }

        setSuccessCount(success);
        setFailedCount(failed);
        setErrors(errorMessages);

        if (failed === 0) {
            // All succeeded
            setTimeout(() => {
                onSuccess();
                onClose();
            }, 1500);
        } else if (success > 0) {
            // Some succeeded, some failed
            setError(`${success} session(s) marked successfully, ${failed} failed`);
        } else {
            // All failed
            setError('Failed to mark sessions as refunded. See details below.');
        }

        setIsSubmitting(false);
    };

    if (!isOpen) {
        return null;
    }

    const isBulk = sessionIds.length > 1;
    const selectedSessions = sessions.filter(s => !!s.sessionId && sessionIds.includes(s.sessionId));

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            {isBulk ? `Mark ${sessionIds.length} Sessions as Refunded` : 'Mark Session as Refunded'}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            {isBulk 
                                ? 'Select a campaign and optionally add a reference for all selected sessions'
                                : 'Select a campaign and optionally add a reference'}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                    {/* Session Details (for single session) */}
                    {!isBulk && selectedSessions.length > 0 && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Session Details</h3>
                            <div className="space-y-1 text-sm text-gray-600">
                                <div><span className="font-medium">Session ID:</span> {selectedSessions[0].sessionId?.substring(0, 16)}...</div>
                                <div><span className="font-medium">License Plate:</span> {selectedSessions[0].licensePlate}</div>
                                <div><span className="font-medium">Area:</span> {selectedSessions[0].areaNo}</div>
                                <div><span className="font-medium">Total:</span> {selectedSessions[0].pricing.total} {selectedSessions[0].pricing.currency}</div>
                            </div>
                        </div>
                    )}

                    {/* Campaign Selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Campaign <span className="text-red-500">*</span>
                        </label>
                        {campaignsLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading campaigns...
                            </div>
                        ) : (
                            <select
                                value={selectedCampaignId}
                                onChange={(e) => setSelectedCampaignId(e.target.value)}
                                disabled={isSubmitting}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="">Select a campaign...</option>
                                {campaigns.map((campaign) => (
                                    <option key={campaign.campaignId} value={campaign.campaignId}>
                                        {campaign.name} ({campaign.campaignId})
                                    </option>
                                ))}
                            </select>
                        )}
                        {campaigns.length === 0 && !campaignsLoading && (
                            <p className="mt-2 text-sm text-gray-500">
                                No campaigns available. Create a campaign first.
                            </p>
                        )}
                    </div>

                    {/* Reference Field */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference (Optional)
                        </label>
                        <input
                            type="text"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            disabled={isSubmitting}
                            placeholder="e.g., PP-REF-12345"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Reference from PP admin (e.g., transaction ID)
                        </p>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-red-800">{error}</p>
                                    {errors.length > 0 && (
                                        <ul className="mt-2 text-xs text-red-700 list-disc list-inside space-y-1">
                                            {errors.map((err, idx) => (
                                                <li key={idx}>{err}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Success Display */}
                    {successCount > 0 && failedCount === 0 && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-green-600" />
                                <p className="text-sm font-medium text-green-800">
                                    Successfully marked {successCount} session(s) as refunded!
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Partial Success Display */}
                    {successCount > 0 && failedCount > 0 && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-yellow-800">
                                        {successCount} session(s) marked successfully, {failedCount} failed
                                    </p>
                                    {errors.length > 0 && (
                                        <ul className="mt-2 text-xs text-yellow-700 list-disc list-inside space-y-1">
                                            {errors.map((err, idx) => (
                                                <li key={idx}>{err}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {failedCount > 0 && successCount > 0 ? 'Close' : 'Cancel'}
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedCampaignId || campaignsLoading}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            'Mark as Refunded'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MarkRefundedModal;

